import type {ReactElement} from 'react';
import {CHECKBOX_TYPE} from '@Components/checkbox';
import {Checkbox} from '@Components/checkbox';
import {Text, TextSize} from '@Components/text';
import {LabelledCheckboxSelectionTheme} from '@Components/labelled-checkbox/labelled-checkbox.types';
import {noop} from '@Utils/general.util';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './labelled-checkbox.module.scss';

export interface LabelledCheckboxProps {
  label: string;
  isChecked: boolean;
  className?: string;
  onChange?: (isChecked: boolean) => void;
  textSize?: TextSize;
  textClassName?: string;
  checkboxClassName?: string;
  checkboxType?: CHECKBOX_TYPE;
  checkboxSize?: IconSize;
  selectionTheme?: LabelledCheckboxSelectionTheme;
  hasHover?: boolean;
  labelIcon?: string;
  isDisabled?: boolean;
  labelInfo?: ReactElement<any>;
  labelIconClassName?: string;
}

export function LabelledCheckbox({
  label,
  checkboxType,
  isChecked = false,
  className = '',
  onChange = noop,
  textSize = TextSize.XSMALL,
  textClassName = '',
  checkboxClassName = '',
  checkboxSize = IconSize.SIZE_ICON_12,
  selectionTheme = LabelledCheckboxSelectionTheme.NONE,
  hasHover = false,
  labelIcon = undefined,
  isDisabled = false,
  labelInfo = undefined,
  labelIconClassName = '',
}: LabelledCheckboxProps): ReactElement<any> {
  const getClassesForSelectionTheme = (): string => {
    switch (selectionTheme) {
      case LabelledCheckboxSelectionTheme.PRIMARY:
        return 'bg-primary-10';

      default:
        return '';
    }
  };

  const getTextColorForSelectionTheme = (): string => {
    switch (selectionTheme) {
      case LabelledCheckboxSelectionTheme.PRIMARY:
        return 'content-primary';

      default:
        return '';
    }
  };

  const shouldTextBeBold = (): boolean => {
    return isChecked && selectionTheme !== LabelledCheckboxSelectionTheme.NONE;
  };

  const getContainerClasses = (): string => {
    const classes = [styles.checkboxRow, 'flex-row-align-center', 'hover-transition-all'];

    if (isChecked) {
      classes.push(`${getClassesForSelectionTheme()} ${styles.checked}`);
    }

    if (isDisabled) {
      classes.push(styles.isDisabled);
    }

    if (hasHover) {
      classes.push(styles.hoverBg);
    }

    return classes.join(' ');
  };

  const getCheckboxType = (): CHECKBOX_TYPE | undefined => {
    if (isDisabled) {
      return CHECKBOX_TYPE.LIGHT;
    }

    return checkboxType;
  };

  const getCheckboxClassName = (): string => {
    if (isDisabled) {
      return `${checkboxClassName} ${styles.disabledCheckbox}`;
    }

    return checkboxClassName;
  };

  const onChangeHander = (): void => {
    if (isDisabled) {
      return;
    }

    onChange(!isChecked);
  };

  return (
    <div className={`${getContainerClasses()} ${className}`} onClick={onChangeHander}>
      <Checkbox className={getCheckboxClassName()} isChecked={isChecked} type={getCheckboxType()} size={checkboxSize} disabled={isDisabled} />
      {labelIcon ? <Icon className={`spacing-m-l-3 ${labelIconClassName}`} icon={labelIcon} size={IconSize.SIZE_ICON_16} /> : undefined}
      <Text
        val={label}
        size={textSize}
        className={`${labelIcon ? 'spacing-m-l-1' : 'spacing-m-l-2'}  ${isChecked ? getTextColorForSelectionTheme() : ''} ${textClassName}`}
        bold={shouldTextBeBold()}
      />
      {labelInfo ?? undefined}
    </div>
  );
}
